import { useState, useEffect, useMemo } from 'react';
import { useAuth } from './AuthContext';
import {
  apiGetStandards, apiCreateStandard,
  apiUpdateStandard, apiDeleteStandard,
  apiGetCodeListByType,
} from './api';
import PageLoader from './PageLoader';
import { formatDate as fmtDate } from './dateUtils';

function truncate(str, n = 280) {
  if (!str) return '';
  return str.length > n ? str.slice(0, n) + '…' : str;
}

// ── Page ──────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 10;

export default function StandardsPage() {
  const { project, user } = useAuth();

  const [standards,    setStandards]    = useState([]);
  const [codeList,     setCodeList]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [search,       setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page,         setPage]         = useState(1);

  const [modal,        setModal]        = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteErr,    setDeleteErr]    = useState('');

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError('');
      try {
        const [stds, codes] = await Promise.all([
          apiGetStandards(project.id),
          apiGetCodeListByType('Standard').catch(() => []),
        ]);
        if (stds?.length) console.log('[Standards] sample item:', stds[0]);
        setStandards(Array.isArray(stds) ? stds : []);
        setCodeList(Array.isArray(codes) ? codes : []);
      } catch {
        setError('Failed to load standards.');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [project.id]);

  const codeMap = useMemo(
    () => Object.fromEntries(
      codeList.map(c => [
        c.codeListId,
        {
          label:       c.codeListSubType || c.codeListDescription || `#${c.codeListId}`,
          description: c.codeListDescription || '',
        },
      ])
    ),
    [codeList]
  );

  const filtered = useMemo(() => {
    setPage(1);
    return standards.filter(s => {
      if (statusFilter === 'active'   && !s.isActive) return false;
      if (statusFilter === 'inactive' && s.isActive) return false;
      if (search && !s.title?.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [standards, search, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages  = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginated   = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  async function handleSave(formData) {
    if (modal.mode === 'new') {
      const created = await apiCreateStandard(project.id, formData, user?.id ?? 0);
      setStandards(prev => [...prev, created]);
    } else {
      await apiUpdateStandard(modal.item.projectStandardId, formData, user?.id ?? 0);
      setStandards(prev => prev.map(s =>
        s.projectStandardId === modal.item.projectStandardId
          ? { ...s, ...formData, isActive: !!formData.isActive }
          : s
      ));
    }
    setModal(null);
  }

  async function handleDelete() {
    setDeleteErr('');
    try {
      await apiDeleteStandard(deleteTarget.projectStandardId);
      setStandards(prev => prev.filter(s => s.projectStandardId !== deleteTarget.projectStandardId));
      setDeleteTarget(null);
    } catch {
      setDeleteErr('Failed to delete. Please try again.');
    }
  }

  const activeCount   = standards.filter(s => s.isActive).length;
  const inactiveCount = standards.filter(s => !s.isActive).length;

  return (
    <div className="customer-window">
      {/* Header */}
      <div className="window-header">
        <div className="window-header-icon">📋</div>
        <div className="window-header-text">
          <h1>Standards</h1>
        </div>
        {/* <button
          className="btn-new"
          onClick={() => setModal({ mode: 'new' })}
          style={{ marginLeft: 'auto' }}
        >
          + New Standard
        </button> */}
      </div>

      <div className="window-body">
        {error && (
          <div className="error-alert" style={{ borderRadius: 'var(--radius-sm)', marginBottom: 'var(--space-md)' }}>
            {error}
          </div>
        )}

        {/* Toolbar */}
        {!loading && !error && (
          <div style={S.toolbar}>
            <input
              type="text"
              autoComplete="off"
              placeholder="Search by title…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={S.searchInput}
            />
            <div style={S.tabs}>
              {[
                { key: 'all',      label: 'All',      count: standards.length },
                { key: 'active',   label: 'Active',   count: activeCount },
                { key: 'inactive', label: 'Inactive', count: inactiveCount },
              ].map(t => (
                <button
                  key={t.key}
                  style={{ ...S.tab, ...(statusFilter === t.key ? S.tabActive : {}) }}
                  onClick={() => setStatusFilter(t.key)}
                >
                  {t.label}
                  <span style={{ ...S.tabBadge, ...(statusFilter === t.key ? S.tabBadgeActive : {}) }}>
                    {t.count}
                  </span>
                </button>
              ))}
            </div>
            <span style={S.resultCount}>
              {filtered.length} standard{filtered.length !== 1 ? 's' : ''}
              {totalPages > 1 && ` · page ${currentPage} of ${totalPages}`}
            </span>
          </div>
        )}


        {loading && <PageLoader label="Loading standards…" />}

        {!loading && !error && filtered.length === 0 && (
          <div className="no-data">
            {standards.length === 0
              ? 'No standards found'
              : 'No standards match the current filter.'}
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <>
            <div style={S.list} key={`${statusFilter}-${search}-${currentPage}`}>
              {paginated.map((s, i) => (
                <div
                  key={s.projectStandardId}
                  style={{
                    animation:      'row-in 0.32s ease both',
                    animationDelay: `${i * 48}ms`,
                  }}
                >
                  <StandardCard standard={s} codeMap={codeMap} />
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div style={S.pagination}>
                <button
                  style={{ ...S.pageBtn, ...(currentPage === 1 ? S.pageBtnDisabled : {}) }}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  ‹ Prev
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                  <button
                    key={n}
                    style={{ ...S.pageBtn, ...(n === currentPage ? S.pageBtnActive : {}) }}
                    onClick={() => setPage(n)}
                  >
                    {n}
                  </button>
                ))}
                <button
                  style={{ ...S.pageBtn, ...(currentPage === totalPages ? S.pageBtnDisabled : {}) }}
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next ›
                </button>
                <span style={S.pageInfo}>
                  {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create / Edit modal */}
      {modal && (
        <StandardModal
          mode={modal.mode}
          item={modal.item}
          codeList={codeList}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div className="modal-overlay">
          <div className="dialog-card">
            <div className="dialog-header">
              <div className="dialog-icon">🗑</div>
              <h3>Delete Standard</h3>
            </div>
            <div className="dialog-details">
              <div className="dialog-detail-row">
                <strong>Title</strong>
                <span>{deleteTarget.title}</span>
              </div>
              <div className="dialog-detail-row">
                <strong>ID</strong>
                <span>S-{deleteTarget.projectStandardId}</span>
              </div>
            </div>
            {deleteErr && (
              <p style={{ fontSize: 12, color: 'var(--error)', marginBottom: 8 }}>{deleteErr}</p>
            )}
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              This action cannot be undone.
            </p>
            <div className="dialog-actions">
              <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Standard card ─────────────────────────────────────────────────────────────

function StandardCard({ standard: s, codeMap }) {
  const [expanded, setExpanded] = useState(false);
  const codeEntry = codeMap[s.fkCodeListStandard];
  const typeName  = codeEntry?.label;
  const typeDesc  = s.codeListDescription || codeEntry?.description || '';
  const isActive  = !!s.isActive;
  const hasMoreContent = (s.content?.length ?? 0) > 280;

  return (
    <div className="card" style={S.card}>
      {/* Card header */}
      <div style={S.cardHeader}>
        <div style={S.cardLeft}>
          <span style={S.idBadge}>{s.fkCodeListStandard ?? '—'}</span>
          <span style={S.cardTitle}>{typeDesc}</span>
          {typeName && <span style={S.typeBadge}>{typeName}</span>}
        </div>
        <div style={S.cardRight}>
          <span style={{ ...S.statusBadge, ...(isActive ? S.statusActive : S.statusInactive) }}>
            {isActive ? '● Active' : '○ Inactive'}
          </span>
        </div>
      </div>

      {/* Content */}
      {(s.title || s.content) ? (
        <div style={S.contentWrap}>
          <pre style={S.contentPre}>
            {s.title && <strong style={{ display: 'block', marginBottom: 6 }}>{s.title}</strong>}
            {expanded ? s.content : truncate(s.content)}
          </pre>
          {hasMoreContent && (
            <button style={S.expandBtn} onClick={() => setExpanded(x => !x)}>
              {expanded ? '▲ Show less' : `▼ Show more (${s.content.length} chars)`}
            </button>
          )}
        </div>
      ) : (
        <p style={S.noContent}>No content.</p>
      )}

      {/* Footer */}
      <div style={S.cardFooter}>
        <span style={S.metaItem}>
          <span style={S.metaLabel}>Created</span>
          <span style={S.metaValue}>
            {fmtDate(s.createdAt)}
            {s.createdByUsername && <span style={S.metaBy}> by {s.createdByUsername}</span>}
          </span>
        </span>
        <span style={S.metaItem}>
          <span style={S.metaLabel}>Updated</span>
          <span style={S.metaValue}>
            {fmtDate(s.updatedAt)}
            {s.updatedByUsername && <span style={S.metaBy}> by {s.updatedByUsername}</span>}
          </span>
        </span>
      </div>
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────────

function StandardModal({ mode, item, codeList, onSave, onClose }) {
  const [title,              setTitle]              = useState(item?.title              || '');
  const [content,            setContent]            = useState(item?.content            || '');
  const [fkCodeListStandard, setFkCodeListStandard] = useState(item?.fkCodeListStandard ?? '');
  const [isActive,           setIsActive]           = useState(item ? !!item.isActive : true);
  const [saving,             setSaving]             = useState(false);
  const [err,                setErr]                = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!title.trim())   { setErr('Title is required.');   return; }
    if (!content.trim()) { setErr('Content is required.'); return; }
    setSaving(true);
    try {
      await onSave({
        title:              title.trim(),
        content:            content.trim(),
        fkCodeListStandard: fkCodeListStandard ? Number(fkCodeListStandard) : null,
        isActive,
      });
    } catch {
      setErr('Failed to save. Please try again.');
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal-card" style={{ maxWidth: 760 }}>
        <button className="modal-close-btn" onClick={onClose} disabled={saving}>×</button>

        <div className="form-header">
          <div className="form-header-info">
            <div className="form-header-avatar">📋</div>
            <div>
              <div className="form-header-name">
                {mode === 'new' ? 'New Standard' : `Edit: ${item.title}`}
              </div>
              <div className="form-header-sub">
                {mode === 'new' ? 'Add a new project standard' : 'Update standard details'}
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-body">
            {err && (
              <div className="error-alert" style={{ borderRadius: 'var(--radius-sm)' }}>
                <span>{err}</span>
                <button className="alert-close" type="button" onClick={() => setErr('')}>×</button>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
              <div className="form-field" style={{ gridColumn: '1 / -1' }}>
                <label>Title <span style={{ color: 'var(--error)' }}>*</span></label>
                <input
                  type="text"
                  placeholder="e.g. Coding Standards"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  autoFocus
                />
              </div>

              {codeList.length > 0 && (
                <div className="form-field">
                  <label>Type</label>
                  <select
                    value={fkCodeListStandard}
                    onChange={e => setFkCodeListStandard(e.target.value)}
                  >
                    <option value="">— Select type —</option>
                    {codeList.map(c => (
                      <option key={c.codeListId} value={c.codeListId}>
                        {c.codeListSubType || c.codeListDescription || `#${c.codeListId}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {mode === 'edit' && (
                <div className="form-field">
                  <label>Status</label>
                  <label style={S.toggleLabel}>
                    <input
                      type="checkbox"
                      checked={isActive}
                      onChange={e => setIsActive(e.target.checked)}
                      style={{ marginRight: 8 }}
                    />
                    {isActive ? 'Active' : 'Inactive'}
                  </label>
                </div>
              )}
            </div>

            <div className="form-field">
              <label>Content <span style={{ color: 'var(--error)' }}>*</span></label>
              <textarea
                placeholder="Enter the full text of this standard…"
                value={content}
                onChange={e => setContent(e.target.value)}
                rows={14}
                style={{ resize: 'vertical', fontFamily: 'var(--font-family)', lineHeight: 1.6 }}
              />
            </div>
          </div>

          <div className="form-actions" style={{ padding: 'var(--space-md) var(--space-lg)' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : mode === 'new' ? 'Create Standard' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const S = {
  toolbar: {
    display: 'flex', alignItems: 'center', gap: 'var(--space-md)',
    marginBottom: 'var(--space-md)', flexWrap: 'wrap',
  },
  searchInput: {
    flex: '0 0 240px', padding: '7px 12px',
    border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-sm)', color: 'var(--text-primary)',
    background: 'var(--bg-page)', outline: 'none',
  },
  tabs: { display: 'flex', gap: 4 },
  tab: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '6px 14px', border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)', background: 'var(--bg-page)',
    color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)',
    fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s',
  },
  tabActive: {
    background: 'var(--info-bg)', border: '1px solid rgba(21,101,192,0.3)',
    color: 'var(--primary)', fontWeight: 700,
  },
  tabBadge: {
    background: 'var(--bg-secondary)', border: '1px solid var(--border)',
    borderRadius: 10, fontSize: 10, fontWeight: 700,
    padding: '1px 6px', color: 'var(--text-muted)',
  },
  tabBadgeActive: {
    background: 'rgba(21,101,192,0.15)', border: '1px solid rgba(21,101,192,0.25)',
    color: 'var(--primary)',
  },
  resultCount: { marginLeft: 'auto', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' },

  statsRow: {
    display: 'flex', gap: 'var(--space-md)', marginBottom: 'var(--space-lg)', flexWrap: 'wrap',
  },
  stat: {
    display: 'flex', alignItems: 'center', gap: 12,
    background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)', padding: '12px 20px',
    flex: '0 0 auto',
  },
  statIcon: { fontSize: 22 },
  statValue: { fontSize: 22, fontWeight: 800, lineHeight: 1.1 },
  statLabel: { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', color: 'var(--text-muted)', marginTop: 2 },

  list: { display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' },
  card: { padding: 'var(--space-lg)' },

  cardHeader: {
    display: 'flex', alignItems: 'flex-start',
    justifyContent: 'space-between', gap: 'var(--space-md)', marginBottom: 10,
  },
  cardLeft: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', flex: 1, minWidth: 0 },
  cardRight: { display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 },

  idBadge: {
    background: 'var(--info-bg)', color: 'var(--info)',
    fontSize: 11, fontWeight: 700, padding: '2px 8px',
    borderRadius: 'var(--radius-pill)', whiteSpace: 'nowrap', flexShrink: 0,
  },
  cardTitle: {
    fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--text-primary)',
  },
  typeBadge: {
    background: 'rgba(156,39,176,0.1)', border: '1px solid rgba(156,39,176,0.25)',
    color: '#7b1fa2', fontSize: 11, fontWeight: 600,
    padding: '2px 9px', borderRadius: 'var(--radius-pill)', whiteSpace: 'nowrap', flexShrink: 0,
  },
  statusBadge: {
    fontSize: 11, fontWeight: 700, padding: '3px 10px',
    borderRadius: 'var(--radius-pill)', whiteSpace: 'nowrap', flexShrink: 0,
  },
  statusActive: {
    background: 'rgba(67,160,71,0.1)', border: '1px solid rgba(67,160,71,0.3)', color: '#2e7d32',
  },
  statusInactive: {
    background: 'rgba(158,158,158,0.1)', border: '1px solid rgba(158,158,158,0.3)', color: 'var(--text-muted)',
  },

  contentWrap: { marginBottom: 12 },
  contentPre: {
    fontFamily: 'var(--font-family)', fontSize: 'var(--font-size-sm)',
    color: 'var(--text-secondary)', lineHeight: 1.7,
    background: 'var(--bg-table-row-alt)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)', padding: 'var(--space-md)',
    whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0,
  },
  expandBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    color: 'var(--primary)', fontSize: 11, fontWeight: 600,
    padding: '4px 0 0', display: 'block', marginTop: 4,
  },
  noContent: {
    fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic', margin: '0 0 12px',
  },

  cardFooter: {
    display: 'flex', alignItems: 'center', gap: 'var(--space-lg)',
    paddingTop: 10, borderTop: '1px solid var(--border)',
  },
  metaItem: { display: 'flex', alignItems: 'center', gap: 6 },
  metaLabel: {
    fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.6px', color: 'var(--text-muted)',
  },
  metaValue: { fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' },
  metaBy: { fontWeight: 600, color: 'var(--text-primary)' },

  toggleLabel: {
    display: 'flex', alignItems: 'center',
    fontSize: 'var(--font-size-sm)', cursor: 'pointer', color: 'var(--text-primary)',
  },

  pagination: {
    display: 'flex', alignItems: 'center', gap: 6,
    marginTop: 'var(--space-lg)', flexWrap: 'wrap',
  },
  pageBtn: {
    padding: '6px 14px', borderRadius: 7,
    border: '1.5px solid var(--border)', background: 'var(--bg-card)',
    color: 'var(--text-primary)', fontSize: 13, fontWeight: 600,
    cursor: 'pointer',
  },
  pageBtnActive: {
    background: 'var(--primary)', color: '#fff', borderColor: 'var(--primary)',
  },
  pageBtnDisabled: {
    opacity: 0.4, cursor: 'default',
  },
  pageInfo: {
    fontSize: 12, color: 'var(--text-muted)', marginLeft: 8, fontWeight: 500,
  },
};
